class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
    this.resizeObserver = null;
    this.positionUpdateTimeout = null;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      // console.log(cartLink);
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });
    setTimeout(() => {
      if (this.querySelector("subcart-drawer"))
        this.querySelector("subcart-drawer").openSubDrawer();
    });
    setTimeout(() => {
      if (this.querySelector("cart-drawer-app"))
        this.querySelector("cart-drawer-app").openSearchApp();
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    // Move focus to drawer for keyboard and screen reader accessibility
    setTimeout(() => {
      // Try heading first (when cart has items), then empty text (when cart is empty)
      const heading = this.querySelector(".drawer__heading") || this.querySelector(".cart__empty-text");
      if (heading) {
        // Make heading focusable
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    }, 100);

    document.body.classList.add("overflow-hidden");
    // Add positioning setup
    this.setupDynamicPositioning();

    // Add mobile resize listener
    this.mobileResizeHandler = () => {
      if (window.innerWidth <= 767) this.setupDynamicPositioning();
    };
    window.addEventListener("resize", this.mobileResizeHandler);
  }

  close() {
    if (this.querySelector("subcart-drawer"))
      this.querySelector("subcart-drawer").closeSubDrawer();
    if (this.querySelector("cart-drawer-app"))
      this.querySelector("cart-drawer-app").closeSearchApp();
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
    // Cleanup observers and listeners
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.mobileResizeHandler) {
      window.removeEventListener("resize", this.mobileResizeHandler);
    }
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
    // Add cleanup before re-render
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Add setup after render
    setTimeout(() => {
      this.setupDynamicPositioning();
    }, 300);
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  connectedCallback() {
    const checkoutBtn = this.querySelector("#CartDrawer-Checkout");
    if (checkoutBtn) {
      checkoutBtn.addEventListener("click", function (event) {
        localStorage.removeItem("selectedCompany");
        document.getElementById("search-box").value = "";
      });
    }
  }

  setupDynamicPositioning() {
    const updatePosition = () => {
      const slider = this.querySelector("slider-component.wbcartupsell");
      const footer = this.querySelector(".drawer__footer");
      const cartDrawer = this.querySelector(".cart-drawer-wrapper");
      const cartDrawerApp = this.querySelector("cart-drawer-app");
      const appWidth = cartDrawer.offsetWidth || 0;
      const appHeight = cartDrawerApp.offsetHeight || 0;
      const dynamicTop =
        (slider?.offsetHeight || 0) + (footer?.offsetHeight || 0);

      document.documentElement.style.setProperty(
        "--dynamic-top",
        `${dynamicTop + 15}px`
      );
      document.documentElement.style.setProperty(
        "--drawer-app-height",
        `${appHeight}px`
      );
      document.documentElement.style.setProperty(
        "--drawer-app-width",
        `${appWidth - 40}px`
      );
    };

    // Create ResizeObserver to track size changes
    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.positionUpdateTimeout);
      this.positionUpdateTimeout = setTimeout(updatePosition, 100);
    });

    // Observe relevant elements
    const slider = this.querySelector("slider-component.wbcartupsell");
    const footer = this.querySelector(".drawer__footer");
    const cartDrawer = this.querySelector(".cart-drawer-wrapper");
    if (slider) this.resizeObserver.observe(slider);
    if (footer) this.resizeObserver.observe(footer);
    if (cartDrawer) this.resizeObserver.observe(cartDrawer);

    // Initial update
    updatePosition();
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

class SubCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.querySelector("#sub_d_close").addEventListener(
      "click",
      this.closeSubDrawer.bind(this)
    );
  }

  closeSubDrawer() {
    this.classList.remove("active");
  }

  openSubDrawer() {
    this.classList.add("animate", "active");
  }
}

customElements.define("subcart-drawer", SubCartDrawer);

class CartDrawerApp extends HTMLElement {
  constructor() {
    super();
  }

  closeSearchApp() {
    this.classList.remove("active");
  }
  openSearchApp() {
    this.classList.add("animate", "active");
  }
}

customElements.define("cart-drawer-app", CartDrawerApp);
